import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Calendar, Shield, LogIn, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { partnerApi } from '../api/client';

interface PartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PartnerApplication {
  telegram: string;
  trafficSources: string;
  views: string;
  subscribers: string;
  description: string;
}

const PartnerModal: React.FC<PartnerModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PartnerApplication>({
    telegram: '',
    trafficSources: '',
    views: '',
    subscribers: '',
    description: ''
  });

  const handleLogin = () => {
    onClose();
    navigate('/login');
  };

  const handleShowForm = () => {
    const token = localStorage.getItem('partner_token');
    if (!token) {
      const steamAuthUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3021'}/partner/auth/steam`;
      window.location.href = steamAuthUrl;
      return;
    }
    setShowForm(true);
  };

  const handleBackToInfo = () => {
    setShowForm(false);
    setFormData({
      telegram: '',
      trafficSources: '',
      views: '',
      subscribers: '',
      description: ''
    });
  };

  const handleInputChange = (field: keyof PartnerApplication, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Отправляем заявку на сервер
      const result = await partnerApi.submitPartnerApplication(formData);
      
      // Показываем сообщение об успехе
      alert('Заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.');
      
      // Закрываем модалку и сбрасываем форму
      onClose();
      setShowForm(false);
      setFormData({
        telegram: '',
        trafficSources: '',
        views: '',
        subscribers: '',
        description: ''
      });
    } catch (error: any) {
      // Ошибка отправки заявки
      const errorMessage = error.response?.data?.message || 'Произошла ошибка при отправке заявки. Попробуйте еще раз.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div 
            className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div 
            className="fixed inset-0 z-[201] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="tesla-card p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Shield size={20} className="text-purple-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-white">
                    {showForm ? 'Заявка на партнерство' : 'Стать партнером'}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors p-1"
                >
                  <X size={20} />
                </button>
              </div>

              {!showForm ? (
                <>
                  {/* Content */}
                  <div className="space-y-4 mb-6">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Calendar size={14} className="text-red-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium mb-1">Важное изменение</h3>
                        <p className="text-gray-400 text-sm">
                          С 1 сентября 2025 года стать партнером можно только после одобрения модерации
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MessageCircle size={14} className="text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium mb-1">Как стать партнером</h3>
                        <p className="text-gray-400 text-sm">
                          Заполните форму заявки с информацией о ваших источниках трафика
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    {/* Кнопка для входа существующих партнеров */}
                    <button
                      onClick={handleLogin}
                      className="w-full tesla-button flex items-center justify-center space-x-2"
                    >
                      <LogIn size={16} />
                      <span>Войти в партнерку</span>
                    </button>
                    
                    {/* Кнопка для подачи заявки */}
                    <button
                      onClick={handleShowForm}
                      className="w-full tesla-button-secondary flex items-center justify-center space-x-2"
                    >
                      <MessageCircle size={16} />
                      <span>Подать заявку</span>
                    </button>
                    
                    {/* Кнопка закрытия */}
                    <button
                      onClick={onClose}
                      className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors border border-white/10 rounded-lg hover:border-white/20"
                    >
                      Закрыть
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Ваш Telegram
                      </label>
                      <input
                        type="text"
                        value={formData.telegram}
                        onChange={(e) => handleInputChange('telegram', e.target.value)}
                        placeholder="@username или +7XXXXXXXXXX"
                        className="w-full tesla-input"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Источники трафика
                      </label>
                      <input
                        type="text"
                        value={formData.trafficSources}
                        onChange={(e) => handleInputChange('trafficSources', e.target.value)}
                        placeholder="YouTube, Instagram, TikTok, сайт и т.д."
                        className="w-full tesla-input"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Просмотры в месяц
                        </label>
                        <input
                          type="text"
                          value={formData.views}
                          onChange={(e) => handleInputChange('views', e.target.value)}
                          placeholder="100K, 1M и т.д."
                          className="w-full tesla-input"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Подписчики
                        </label>
                        <input
                          type="text"
                          value={formData.subscribers}
                          onChange={(e) => handleInputChange('subscribers', e.target.value)}
                          placeholder="10K, 100K и т.д."
                          className="w-full tesla-input"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Дополнительная информация
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Расскажите о себе, опыте работы, планах по продвижению..."
                        className="w-full tesla-textarea h-24"
                        required
                      />
                    </div>

                    {/* Form Actions */}
                    <div className="space-y-3 pt-2">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full tesla-button flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Отправка...</span>
                          </>
                        ) : (
                          <>
                            <Send size={16} />
                            <span>Отправить заявку</span>
                          </>
                        )}
                      </button>
                      
                      <button
                        type="button"
                        onClick={handleBackToInfo}
                        className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors border border-white/10 rounded-lg hover:border-white/20"
                      >
                        Назад
                      </button>
                    </div>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PartnerModal;